import React, { useRef, useCallback, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Utiliser des attributs "style" (CSS inline) plutôt que les classes Quill.
// Objectif : conserver la mise en forme collée depuis Word (police, taille,
// couleur, alignement, indentation) et la garder visible hors de l'éditeur,
// dans le rendu final de l'article.
const AlignStyle = Quill.import('attributors/style/align');
const DirectionStyle = Quill.import('attributors/style/direction');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
const ColorStyle = Quill.import('attributors/style/color');
const BackgroundStyle = Quill.import('attributors/style/background');

// Autoriser toutes les valeurs : Word utilise des polices et des tailles
// arbitraires que l'on veut préserver telles quelles lors du collage.
FontStyle.whitelist = null;
SizeStyle.whitelist = null;

Quill.register(AlignStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);
Quill.register(ColorStyle, true);
Quill.register(BackgroundStyle, true);

/**
 * Convertit du texte format markdown en HTML.
 * Gère : sauts de ligne, **gras**, *italique*, listes à puces, emojis.
 */
function markdownToHtml(text) {
  if (!text) return '';

  // Échapper les caractères HTML
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Convertir **gras** → <strong>
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Convertir *italique* → <em> (mais pas les ** déjà traités)
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  // Séparer en lignes
  const lines = html.split('\n');

  // Reconstruire avec des <p> et <br>
  let result = '';
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    if (line === '') {
      // Ligne vide = nouveau paragraphe
      result += '</p><p><br></p><p>';
      i++;
      continue;
    }

    // Ajouter la ligne
    if (i > 0 && lines[i - 1].trim() !== '') {
      result += '<br>';
    }
    result += line;
    i++;
  }

  // Envelopper dans un <p>
  result = '<p>' + result + '</p>';

  // Nettoyer les paragraphes vides en début/fin
  result = result.replace(/^<p><\/p>/, '').replace(/<p><\/p>$/, '');
  result = result.replace(/<p><br><\/p>/g, '<p><br></p>');

  return result;
}

/**
 * Détecte si le texte est du markdown (contient des ** ou des sauts de ligne structurés)
 */
function isMarkdownLike(text) {
  if (!text) return false;
  const hasMarkdownBold = /\*\*.+?\*\*/.test(text);
  const hasMultipleLines = (text.match(/\n/g) || []).length >= 2;
  return hasMarkdownBold || hasMultipleLines;
}

const modules = {
  toolbar: [
    [{ font: [] }, { size: ['12px', '14px', '16px', false, '20px', '24px', '32px'] }],
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

function RichTextEditor({ value, onChange, placeholder }) {
  const quillRef = useRef(null);

  const handlePaste = useCallback((e) => {
    // Si du HTML riche est disponible (copie depuis Word, navigateur), laisser Quill gérer
    const htmlData = e.clipboardData.getData('text/html');
    if (htmlData && htmlData.trim()) {
      // Quill gère nativement le HTML riche (depuis Word, navigateurs web, etc.)
      return;
    }

    // Sinon, c'est du texte brut (depuis ChatGPT texte, terminal, Notepad, etc.)
    const plainText = e.clipboardData.getData('text/plain');
    if (plainText && isMarkdownLike(plainText)) {
      e.preventDefault();
      e.stopPropagation();

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const convertedHtml = markdownToHtml(plainText);

      // Supprimer la sélection actuelle si elle existe
      const range = editor.getSelection();
      if (range) {
        if (range.length > 0) {
          editor.deleteText(range.index, range.length);
        }
        editor.clipboard.dangerouslyPasteHTML(range.index, convertedHtml, 'user');
      } else {
        editor.clipboard.dangerouslyPasteHTML(convertedHtml, 'user');
      }
    }
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Éléments de bloc porteurs d'un alignement de paragraphe.
    // On limite le traitement à ces balises : appliquer l'alignement aux
    // éléments inline (span, strong, a...) déforme le rendu des listes Word.
    const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'TD', 'TH']);

    const Delta = Quill.import('delta');

    // 1) Préserver l'alignement (justifié, centré, à droite) collé depuis Word.
    //    Word place l'alignement en style inline (text-align) ou via l'attribut
    //    HTML "align". Quill ne le récupère pas toujours seul.
    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (!BLOCK_TAGS.has(node.tagName)) return delta;

      const styleAlign = node.style && node.style.textAlign;
      const attrAlign = node.getAttribute && node.getAttribute('align');
      const align = (styleAlign || attrAlign || '').toLowerCase();

      if (align === 'justify' || align === 'center' || align === 'right' || align === 'left') {
        return new Delta(
          delta.ops.map((op) => ({
            ...op,
            attributes: { ...(op.attributes || {}), align }
          }))
        );
      }
      return delta;
    });

    // 2) Neutraliser le retrait de première ligne (text-indent) et les marges
    //    négatives que Word ajoute aux puces : c'est ce qui décale/centre
    //    visuellement les listes après le collage.
    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (node.style) {
        node.style.textIndent = '';
        node.style.marginLeft = '';
        node.style.marginRight = '';
      }
      return delta;
    });

    const editorRoot = editor.root;
    editorRoot.addEventListener('paste', handlePaste, true);

    return () => {
      editorRoot.removeEventListener('paste', handlePaste, true);
    };
  }, [handlePaste]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      modules={modules}
    />
  );
}

export default RichTextEditor;
